import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  getPreferences,
  getUnreadCount,
  markAsClicked,
  Notification,
  subscribeNotificationPreferencesChanged,
  subscribeNotificationsChanged,
} from '../../services/notifications';
import NotificationItem from './NotificationItem';
import NotificationToast from './NotificationToast';
import DashboardLoader from '../common/DashboardLoader';

const POLL_INTERVAL_MS = 30_000;
const TOAST_SEVERITIES: ReadonlyArray<Notification['severity']> = ['high', 'critical'];

/** Bell badge + popover. Owns unread-count polling and toast surfacing for new high/critical alerts. */
const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [toastNotification, setToastNotification] = useState<Notification | null>(null);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  /** Latest unread count, used by poll/event paths to detect new arrivals without re-running effects. */
  const previousUnreadCountRef = useRef<number>(0);
  /** Tracks last toast id so the same notification cannot toast twice on subsequent polls. */
  const lastToastNotificationIdRef = useRef<string | null>(null);
  /** Read inside callbacks so toggling preferences does not re-run the polling effect. */
  const showToastRef = useRef<boolean>(true);
  /** Cancels in-flight list fetches when superseded. */
  const listAbortRef = useRef<AbortController | null>(null);

  const fetchPreferences = useCallback(async (signal?: AbortSignal) => {
    try {
      const preferences = await getPreferences({ signal });
      showToastRef.current = Boolean(preferences.show_toast && preferences.in_app_enabled);
    } catch (error) {
      if ((error as { code?: string }).code === 'ERR_CANCELED') return;
      console.error('Failed to fetch notification preferences:', error);
      showToastRef.current = true;
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    try {
      setLoading(true);
      const response = await getNotifications(
        { limit: 10, archived: false },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      setNotifications(response.data.notifications);
    } catch (error) {
      if ((error as { code?: string }).code === 'ERR_CANCELED') return;
      console.error('Failed to fetch notifications:', error);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  /** When the unread count grows, surface the latest unread high/critical via toast. */
  const maybeShowToastForNewUnread = useCallback(async (count: number) => {
    if (!showToastRef.current) return;
    if (count <= previousUnreadCountRef.current) return;
    try {
      const response = await getNotifications({ limit: 1, archived: false, read: false });
      const latest = response.data.notifications[0];
      if (!latest) return;
      if (!TOAST_SEVERITIES.includes(latest.severity)) return;
      if (lastToastNotificationIdRef.current === latest.id) return;
      lastToastNotificationIdRef.current = latest.id;
      setToastNotification(latest);
      setToastOpen(true);
    } catch (error) {
      console.error('Failed to fetch latest unread notification for toast:', error);
    }
  }, []);

  const refreshUnreadCount = useCallback(
    async (options?: { maybeToast?: boolean; signal?: AbortSignal }) => {
      try {
        const count = await getUnreadCount({ signal: options?.signal });
        if (options?.maybeToast) {
          await maybeShowToastForNewUnread(count);
        }
        previousUnreadCountRef.current = count;
        setUnreadCount(count);
      } catch (error) {
        if ((error as { code?: string }).code === 'ERR_CANCELED') return;
        console.error('Failed to fetch unread count:', error);
      }
    },
    [maybeShowToastForNewUnread]
  );

  // Mount-only setup: initial fetch, polling, visibility-aware pausing, and event subscriptions.
  // Keeping the dep list empty avoids restarting the interval/listeners on every popover toggle.
  useEffect(() => {
    const controller = new AbortController();
    void fetchPreferences(controller.signal);
    void refreshUnreadCount({ signal: controller.signal });

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void refreshUnreadCount({ maybeToast: true });
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId === null) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        void refreshUnreadCount({ maybeToast: true });
        startPolling();
      }
    };

    if (typeof document !== 'undefined' && !document.hidden) {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const unsubscribeNotificationsChanged = subscribeNotificationsChanged(() => {
      void refreshUnreadCount();
    });
    const unsubscribePreferencesChanged = subscribeNotificationPreferencesChanged(() => {
      void fetchPreferences();
    });

    return () => {
      controller.abort();
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unsubscribeNotificationsChanged();
      unsubscribePreferencesChanged();
      listAbortRef.current?.abort();
    };
  }, [fetchPreferences, refreshUnreadCount]);

  // Fetch the popover list only when it actually opens. Unread count is already up to date
  // from polling and event-driven refreshes, so we don't refetch it here.
  useEffect(() => {
    if (!open) return;
    void fetchNotifications();
  }, [open, fetchNotifications]);

  // While the popover is open, also refresh the list whenever something changes server-side.
  useEffect(() => {
    if (!open) return;
    return subscribeNotificationsChanged(() => {
      void fetchNotifications();
    });
  }, [open, fetchNotifications]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      const updated = await markAsClicked(notification.id);
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? updated : n)));
      if (!notification.read) {
        const next = Math.max(0, previousUnreadCountRef.current - 1);
        previousUnreadCountRef.current = next;
        setUnreadCount(next);
      }
      if (notification.action_url) {
        navigate(notification.action_url);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to mark notification as clicked:', error);
    }
  };

  const handleViewAll = () => {
    navigate('/notifications');
    handleClose();
  };

  const handleToastAction = () => {
    if (!toastNotification) return;
    void handleNotificationClick(toastNotification);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          color="inherit"
          onClick={handleClick}
          sx={{ position: 'relative' }}
          aria-label="Notifications"
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            maxHeight: '80vh',
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {unreadCount} unread
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 1 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <DashboardLoader size={56} minHeight="auto" />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => {
                    void handleNotificationClick(notification);
                  }}
                />
              ))}
            </List>
          )}

          {notifications.length > 0 && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button size="small" onClick={handleViewAll}>
                  View All Notifications
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>

      <NotificationToast
        notification={toastNotification}
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        onAction={handleToastAction}
      />
    </>
  );
};

export default NotificationBell;
